import { z } from 'zod';

/** Retire espaces, tirets et points — les identifiants métier sont souvent saisis avec séparateurs. */
export function normaliserIdentifiant(valeur: string): string {
  return valeur.replace(/[\s.\-_/]/g, '').toUpperCase();
}

/**
 * NIF / NIU : AUCUN contrôle de format (les formats ont varié selon les années et les administrations).
 * La valeur est conservée telle que saisie (espaces de début et de fin retirés) ; seule la présence est exigée
 * là où le champ est obligatoire.
 */
export const nifGabon = z.string().trim().min(1, 'NIF obligatoire');

/**
 * Affiliation CNSS / CNAMGS : AUCUN contrôle de format ni de longueur (numéros provisoires, anciens formats).
 * La valeur est conservée telle que saisie (espaces de début et de fin retirés) ; seule la présence est exigée
 * là où le champ est obligatoire.
 */
export const numeroCnss = z.string().trim().min(1, 'N° CNSS obligatoire');

export const numeroCnamgs = z.string().trim().min(1, 'N° CNAMGS obligatoire');

/**
 * RCCM : AUCUN contrôle de format (GA-LBV-2023-B-12345, RCCM-…, LBV-…, numéros anciens : les formats diffèrent
 * beaucoup selon les années). La valeur est conservée telle que saisie ; seule la présence est exigée là où le champ
 * est obligatoire.
 */
export const rccmGabon = z.string().trim().min(1, 'RCCM obligatoire');

/**
 * Téléphone : AUCUN contrôle de format (numéros locaux ou étrangers, avec ou sans indicatif). Valeur conservée telle
 * que saisie (espaces de début et de fin retirés).
 */
export const telephoneGabon = z
  .string()
  .trim()
  .min(1, 'Téléphone requis')
  .max(40, '40 caractères maximum');

/** Nom / prénom : AUCUN contrôle de caractères (noms composés, abréviations, points, chiffres éventuels). */
export const nomPersonne = z
  .string()
  .trim()
  .min(1, 'Nom requis')
  .max(100, '100 caractères maximum');

export type TypePieceIdentite = 'CNI' | 'Passeport' | 'Carte sejour';

export interface PieceIdentiteMeta {
  label: string;
  placeholder: string;
  hint: string;
  min: number;
  max: number;
}

/**
 * Libellés des pièces d'identité. AUCUN contrôle de format : les numéros (CNI, passeport, titre de séjour) varient
 * selon l'année et le pays de délivrance. La valeur est conservée telle que saisie.
 */
export const PIECE_IDENTITE_META: Record<TypePieceIdentite, PieceIdentiteMeta> = {
  CNI: {
    label: 'N° CNI',
    placeholder: 'N° de la carte nationale d’identité',
    hint: 'Format libre',
    min: 0,
    max: 40,
  },
  Passeport: {
    label: 'N° passeport',
    placeholder: 'N° du passeport',
    hint: 'Format libre',
    min: 0,
    max: 40,
  },
  'Carte sejour': {
    label: 'N° titre de séjour',
    placeholder: 'N° du titre de séjour',
    hint: 'Format libre',
    min: 0,
    max: 40,
  },
};

export function metaPieceIdentite(type: string | undefined): PieceIdentiteMeta {
  if (type === 'Passeport' || type === 'Carte sejour' || type === 'CNI') {
    return PIECE_IDENTITE_META[type];
  }
  return PIECE_IDENTITE_META.CNI;
}

/** Numéro de pièce : aucun contrôle de format (toujours accepté, vide compris). */
export function validerNumeroPiece(
  _type: string | undefined,
  _numero: string | undefined,
): string | null {
  return null;
}

/** IBAN / RIB : AUCUN contrôle de format (IBAN gabonais, RIB local, compte étranger). Valeur conservée telle que saisie. */
export const ibanGabon = z
  .string()
  .trim()
  .min(1, 'IBAN / RIB requis')
  .max(40, '40 caractères maximum');

/** Vérifie la clé de contrôle IBAN (ISO 13616 / MOD 97). */
export function validerIbanMod97(iban: string): boolean {
  const compact = iban.replace(/\s/g, '').toUpperCase();
  if (compact.length < 15) return false;
  const rearranged = compact.slice(4) + compact.slice(0, 4);
  let expanded = '';
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    if (code >= 65 && code <= 90) expanded += String(code - 55);
    else expanded += ch;
  }
  let remainder = 0;
  for (const digit of expanded) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

/** Plaque d'immatriculation : AUCUN contrôle de format (anciennes et nouvelles plaques). */
export const plaqueGabon = z
  .string()
  .trim()
  .min(1, 'Immatriculation requise')
  .max(30, '30 caractères maximum');

/** Permis de conduire : AUCUN contrôle de format. */
export const permisConduireGabon = z
  .string()
  .trim()
  .min(1, 'Permis de conduire requis')
  .max(40, '40 caractères maximum');

/** Permis de travail : AUCUN contrôle de format. */
export const permisTravailGabon = z
  .string()
  .trim()
  .min(1, 'Permis de travail requis')
  .max(40, '40 caractères maximum');

/** Email générique (tous formulaires). */
export const emailStrict = z
  .string()
  .trim()
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Adresse email invalide');

/** Code article stock. */
export const codeArticleStock = z
  .string()
  .trim()
  .min(1, 'Code article requis')
  .max(50, '50 caractères maximum');

export const UNITES_STOCK = [
  'kg',
  'tonne',
  'litre',
  'unité',
  'm²',
  'm³',
  'boîte',
  'palette',
] as const;

/** Compte SYSCOHADA : 2 à 8 chiffres. */
export const compteOhada = z.string().regex(/^\d{2,8}$/, 'Compte OHADA invalide (2 à 8 chiffres)');
